import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { useOwnSettings } from '@/hooks/useSettings';
import type { UserSettings } from '@/services/settingsClient';

export const USER_DATE_FORMATS = ['MMM d, yyyy', 'yyyy-MM-dd', 'MM/dd/yyyy', 'dd/MM/yyyy'] as const;
export const DEFAULT_USER_PREFERENCES: UserSettings['preferences'] = { timezone: 'America/Denver', dateFormat: 'MMM d, yyyy', landingPage: '/', denseMode: true, emailDigestEnabled: false, criticalOnlyNotifications: false };

function validTimeZone(timezone: string): string {
  try { new Intl.DateTimeFormat('en-US', { timeZone: timezone }).format(); return timezone; }
  catch { return DEFAULT_USER_PREFERENCES.timezone; }
}

export function formatPreferenceDate(value: string | Date | null | undefined, preferences = DEFAULT_USER_PREFERENCES, includeTime = false): string {
  if (!value) return '—';
  if (value instanceof Date && Number.isNaN(value.getTime())) return '—';
  const text = value instanceof Date ? value.toISOString() : value;
  const dateOnly = /^\d{4}-\d{2}-\d{2}$/.test(text);
  const date = new Date(dateOnly ? `${text}T12:00:00.000Z` : text);
  if (Number.isNaN(date.getTime())) return text;
  const timezone = dateOnly ? 'UTC' : validTimeZone(preferences.timezone);
  const parts = new Intl.DateTimeFormat('en-US', { timeZone: timezone, year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(date);
  const part = (type: string) => parts.find((item) => item.type === type)?.value ?? '';
  let formatted: string;
  switch (preferences.dateFormat) {
    case 'yyyy-MM-dd': formatted = `${part('year')}-${part('month')}-${part('day')}`; break;
    case 'MM/dd/yyyy': formatted = `${part('month')}/${part('day')}/${part('year')}`; break;
    case 'dd/MM/yyyy': formatted = `${part('day')}/${part('month')}/${part('year')}`; break;
    default: formatted = new Intl.DateTimeFormat('en-US', { timeZone: timezone, year: 'numeric', month: 'short', day: 'numeric' }).format(date); if (preferences.dateFormat === 'MMM d yyyy') formatted = formatted.replace(',', '');
  }
  return includeTime && !dateOnly ? `${formatted} · ${new Intl.DateTimeFormat('en-US', { timeZone: timezone, hour: 'numeric', minute: '2-digit', timeZoneName: 'short' }).format(date)}` : formatted;
}

interface UserPreferencesContextValue {
  preferences: UserSettings['preferences'] | null;
  loading: boolean;
  error: unknown;
  formatDate: (value: string | Date | null | undefined) => string;
  formatDateTime: (value: string | Date | null | undefined) => string;
}
const defaultContext: UserPreferencesContextValue = { preferences: null, loading: false, error: null, formatDate: formatPreferenceDate, formatDateTime: (value) => formatPreferenceDate(value, DEFAULT_USER_PREFERENCES, true) };
const UserPreferencesContext = createContext<UserPreferencesContextValue>(defaultContext);

/** Authenticated own preferences; styling is applied only by AppShell. */
export function UserPreferencesProvider({ children }: { children: ReactNode }) {
  const query = useOwnSettings();
  const preferences = query.data?.preferences ?? null;
  const value = useMemo<UserPreferencesContextValue>(() => ({
    preferences, loading: query.isLoading, error: query.error,
    formatDate: (input) => formatPreferenceDate(input, preferences ?? DEFAULT_USER_PREFERENCES),
    formatDateTime: (input) => formatPreferenceDate(input, preferences ?? DEFAULT_USER_PREFERENCES, true),
  }), [preferences, query.error, query.isLoading]);
  return <UserPreferencesContext.Provider value={value}>{children}</UserPreferencesContext.Provider>;
}
export function useUserPreferences() { return useContext(UserPreferencesContext); }
